import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy } from "lucide-react";

import { Avatar } from "@gately/ui/avatar";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";
import {
  formatRecurringSchedule,
  formatVisitInstant,
  formatVisitRange,
  viewerTimeZone,
} from "@gately/validators";

import type {
  ExpectedGuestRow,
  ExpectedGuestStatus,
} from "~/lib/expected-guests";
import { ConfirmArrivalButton } from "~/components/visitors/confirm-arrival-button";
import { VisitorBadges } from "~/components/visitors/visitor-badges";
import {
  groupExpectedGuestsByDay,
  isInProgress,
  RECURRING_GROUP_LABEL,
  upcomingExpectedGuests,
} from "~/lib/expected-guests";
import { orpc } from "~/lib/orpc";
import { passUrl } from "~/lib/pass-share";
import { useOrgTimeZone } from "~/lib/use-me";
import { usePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/attendus")({
  // Prefetch in the loader so the component's useSuspenseQuery has data ready
  // and never suspends mid-navigation (which blanks/remounts the whole tree).
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      orpc.invite.list.queryOptions({ input: { limit: 100 } }),
    ),
  component: AttendusPage,
});

// Rows here already come pre-grouped by day (viewer-local, via
// groupExpectedGuestsByDay/upcomingExpectedGuests in ~/lib/expected-guests —
// deliberately unchanged). What's formatted below is the time-of-day (and,
// for recurring, the span) of the row's own validFrom/validTo, a stored
// validity instant whose time-of-day is authored/evaluated in the
// ORGANIZATION's zone (GATELY-40) — display must agree, which is what the
// shared zoned formatter guarantees.

// Resolved once per module, not per render.
const VIEWER = viewerTimeZone();

function sameOrgDay(a: string | Date, b: string | Date, timeZone: string) {
  const key = (d: string | Date) =>
    new Date(d).toLocaleDateString("en-US", { timeZone });
  return key(a) === key(b);
}

/** Time window for a row already grouped under a day heading — the date
 *  itself is conveyed by the group's heading, so only the time-of-day
 *  matters here. */
function timeWindowLabel(
  row: ExpectedGuestRow,
  timeZone: string,
  now: Date,
): { text: string; secondary: string | null } {
  if (!row.validFrom) return { text: "—", secondary: null };
  // The window already opened: the start instant is history, the end is
  // what the desk needs.
  if (isInProgress(row, now)) {
    if (!row.validTo) return { text: "En cours", secondary: null };
    const to = formatVisitInstant(row.validTo, timeZone, {
      viewerTimeZone: VIEWER,
    });
    return {
      text: `En cours · jusqu'au ${dateOnly(row.validTo, timeZone)}, ${to.primary}`,
      secondary: to.secondary,
    };
  }
  if (row.validTo && sameOrgDay(row.validFrom, row.validTo, timeZone)) {
    // Both ends via one call — composing this from two independent
    // `formatVisitInstant` calls would print the zone label twice.
    const range = formatVisitRange(row.validFrom, row.validTo, timeZone, {
      viewerTimeZone: VIEWER,
    });
    return { text: range.primary, secondary: range.secondary };
  }
  const from = formatVisitInstant(row.validFrom, timeZone, {
    viewerTimeZone: VIEWER,
  });
  return { text: `À partir de ${from.primary}`, secondary: from.secondary };
}

// Recurring's overall span is calendar-day bounds only (00:00/23:59
// placeholders — see buildValidity), never a real clock reading, so there is
// no instant here for `formatVisitInstant` to label a zone on.
function dateOnly(d: string | Date, timeZone: string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    timeZone,
  });
}

/** Overall start–end span of a recurring invite (not a single day's window —
 *  that's conveyed by the weekday codes instead). */
function recurringWindowLabel(row: ExpectedGuestRow, timeZone: string): string {
  if (!row.validFrom) return "—";
  if (!row.validTo) return `À partir du ${dateOnly(row.validFrom, timeZone)}`;
  return `${dateOnly(row.validFrom, timeZone)} – ${dateOnly(row.validTo, timeZone)}`;
}

const STATUS_TONE: Record<
  ExpectedGuestStatus,
  "success" | "info" | "warning" | "neutral" | "danger"
> = {
  active: "success",
  upcoming: "info",
  pending: "warning",
  rejected: "danger",
  expired: "neutral",
  revoked: "danger",
  used: "neutral",
  off_schedule: "warning",
  incomplete: "warning",
};

const STATUS_LABEL: Record<ExpectedGuestStatus, string> = {
  active: "Actif",
  upcoming: "À venir",
  pending: "En attente d'approbation",
  rejected: "Refusé",
  expired: "Expiré",
  revoked: "Révoqué",
  used: "Utilisé",
  off_schedule: "Hors créneau",
  incomplete: "Infos non complétées",
};

/** A row in one of the dated day groups (one_time/date_range), or in the
 *  trailing recurring-access group — the recurring case swaps the time
 *  window for weekday codes + the overall start–end span. */
function GuestRow({ row }: { row: ExpectedGuestRow }) {
  // Read from the cached `me.get` — no extra fetch, and the venue's zone
  // rather than the reader's browser decides every hour printed below.
  const timeZone = useOrgTimeZone();
  // Display gating only — the server re-checks. A résident's own /attendus
  // has no « Arrivé »: they cannot record an entry, and offering the button
  // would only earn them a 403.
  const canConfirm = usePermissions().canConfirmArrival;
  const isRecurring = row.type === "recurring";
  // Hoisted so the narrowing survives into the click handler.
  const token = row.token;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-3 last:border-0">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar name={row.guestName} className="size-9 shrink-0 text-[12px]" />
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-medium text-neutral-950">
            {row.guestName}
          </div>
          <div className="truncate text-[12px] text-neutral-400">
            {row.unitLabel}
            {isRecurring
              ? ` · ${
                  formatRecurringSchedule(
                    {
                      recurrence: row.recurrence ?? null,
                      dailyStartMinutes: row.dailyStartMinutes,
                      dailyEndMinutes: row.dailyEndMinutes,
                    },
                    timeZone,
                  ) || "—"
                }`
              : ""}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {isRecurring ? (
          <span className="font-mono text-[12.5px] text-neutral-500">
            {recurringWindowLabel(row, timeZone)}
          </span>
        ) : (
          (() => {
            const { text, secondary } = timeWindowLabel(
              row,
              timeZone,
              new Date(),
            );
            return (
              <span
                className="font-mono text-[12.5px] text-neutral-500"
                title={secondary ?? undefined}
              >
                {text}
              </span>
            );
          })()
        )}
        <VisitorBadges visitor={row.visitor} />
        <StatusBadge tone={STATUS_TONE[row.status]}>
          {STATUS_LABEL[row.status]}
        </StatusBadge>
        {/* `invite.list` — this page's only source — always selects `token`.
            It is optional on the row type because the gate station's
            `expected.today` deliberately does not send it, so there is nothing
            to copy when it is absent. */}
        {token ? (
          <button
            type="button"
            aria-label="Copier le lien"
            onClick={() => {
              void navigator.clipboard
                .writeText(passUrl(token))
                .then(() => toast.success("Lien copié."));
            }}
            className="flex size-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <Copy size={14} strokeWidth={1.85} />
          </button>
        ) : null}
        {canConfirm ? <ConfirmArrivalButton row={row} /> : null}
      </div>
    </div>
  );
}

function AttendusPage() {
  const { data: invites } = useSuspenseQuery(
    orpc.invite.list.queryOptions({ input: { limit: 100 } }),
  );
  const terms = useTerms();
  const timeZone = useOrgTimeZone();

  const now = new Date();
  const groups = groupExpectedGuestsByDay(
    // Reception's « Arrivé » list: masked, like the desk.
    upcomingExpectedGuests(invites, timeZone, now, terms.orgLabel, "masked"),
    timeZone,
    now,
  );
  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">
          Visiteurs attendus
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {total} visiteur{total !== 1 ? "s" : ""} à venir
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-[14px] border border-neutral-200 bg-white px-5 py-12 text-center text-sm text-neutral-400">
          Aucun visiteur attendu pour le moment.
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.label}>
              <h2 className="mb-2 text-[13px] font-semibold text-neutral-700">
                {group.label}
              </h2>
              {group.label === RECURRING_GROUP_LABEL ? (
                <p className="-mt-1 mb-2 text-[12px] text-neutral-400">
                  Accès qui se répètent chaque semaine, quel que soit leur point
                  de départ.
                </p>
              ) : null}
              <div className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white">
                {group.rows.map((row) => (
                  <GuestRow key={row.guestId} row={row} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
